import React, { useState } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords } from '../../hooks/useHmsRecords';
import { ipApi } from '../../services/api';

/* ═══════════════════════════════════════════════════════════
   TELEMEDICINE / VIDEO CONSULT MODULE
   - Video Consultation Lobby
   - e-Prescription after call
   - Follow-up Scheduling
   - Patient Queue & Status
   - Consultation History
   - Telemedicine Guidelines (MCI/NMC)
═══════════════════════════════════════════════════════════ */

function VideoCallRoom({ patient, onEnd, onSaveNotes }) {
  const [callActive, setCallActive] = useState(true);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [notes, setNotes] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  React.useEffect(() => {
    if (!callActive) return;
    const timer = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timer);
  }, [callActive]);

  const formatTime = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 14, minHeight: 500 }}>
      <div className="info-banner full" style={{ gridColumn: '1 / -1' }}>
        Preview mode — live video requires WebRTC provider integration. Queue booking and e-prescription below are fully functional.
      </div>
      <div style={{ background: '#0f172a', borderRadius: 16, overflow: 'hidden', position: 'relative', minHeight: 420, display: 'flex', flexDirection: 'column' }}>
        <div style={{ position: 'absolute', top: 14, left: 14, zIndex: 2, display: 'flex', gap: 8 }}>
          <span style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>⏱ PREVIEW {formatTime(elapsed)}</span>
          <span style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '4px 12px', borderRadius: 999, fontSize: 12 }}>{patient.patient}</span>
        </div>
        <div style={{ flex: 1, background: `radial-gradient(ellipse at 30% 40%, #1e3a5f 0%, #0f172a 70%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 100, height: 100, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, boxShadow: '0 0 40px rgba(99,102,241,0.4)' }}>
            {patient.gender === 'M' ? '👨' : '👩'}
          </div>
        </div>
        <div style={{ position: 'absolute', bottom: 80, right: 16, width: 120, height: 90, background: '#1e293b', borderRadius: 10, border: '2px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30 }}>
          {camOff ? '📷' : '👨‍⚕️'}
        </div>
        <div style={{ background: 'rgba(0,0,0,0.8)', padding: '14px 20px', display: 'flex', justifyContent: 'center', gap: 12 }}>
          {[
            { icon: muted ? '🔇' : '🎙', label: muted ? 'Unmute' : 'Mute', action: () => setMuted(m => !m), active: muted },
            { icon: camOff ? '📷' : '📹', label: camOff ? 'Start Cam' : 'Stop Cam', action: () => setCamOff(c => !c), active: camOff },
            { icon: '💬', label: 'Chat', action: () => { }, active: false },
            { icon: '🖥', label: 'Screen Share', action: () => { }, active: false },
            { icon: '📞', label: 'End Call', action: () => { setCallActive(false); onEnd(); }, color: '#dc2626' },
          ].map(btn => (
            <button key={btn.label} type="button" onClick={btn.action} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '8px 16px', background: btn.color || (btn.active ? '#475569' : 'rgba(255,255,255,0.1)'), border: 'none', borderRadius: 10, cursor: 'pointer', color: '#fff', fontSize: 11 }}>
              <span style={{ fontSize: 22 }}>{btn.icon}</span>
              {btn.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card" style={{ borderLeft: '4px solid #6366f1' }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{patient.patient}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{patient.age}y / {patient.gender === 'M' ? 'Male' : 'Female'} · {patient.city}</div>
          <div style={{ marginTop: 8, padding: '8px 10px', background: '#f8fafc', borderRadius: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 700, color: '#0d6e6a', marginBottom: 2 }}>Chief Complaint:</div>
            <div>{patient.complaint}</div>
          </div>
        </div>
        <div className="card" style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13, color: '#475569' }}>📝 Consultation Notes</div>
          <textarea
            className="input"
            rows={8}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="S: Chief complaint...\nO: Vitals, findings...\nA: Assessment/Diagnosis...\nP: Plan / Treatment..."
            style={{ fontSize: 13, lineHeight: 1.6 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-primary" style={{ flex: 1 }} disabled={saveBusy || !notes.trim()} onClick={async () => {
            if (!onSaveNotes) return;
            setSaveBusy(true);
            try {
              await onSaveNotes({ patientName: patient.patient, patientId: patient.patientId, notes, durationSec: elapsed });
              setNotesSaved(true);
              setTimeout(() => setNotesSaved(false), 2500);
            } finally { setSaveBusy(false); }
          }}>{saveBusy ? 'Saving…' : notesSaved ? '✓ Saved' : '📋 Save Notes'}</button>
        </div>
      </div>
    </div>
  );
}

function EPrescribeModal({ patient, onClose, onSave }) {
  const [rxLines, setRxLines] = useState([{ drug: '', dose: '', freq: 'OD', days: 5, instructions: 'After meals' }]);
  const [advice, setAdvice] = useState('');
  const [followUp, setFollowUp] = useState('2 weeks');
  const FREQ = ['OD', 'BD', 'TDS', 'QID', 'HS', 'SOS', 'Weekly', 'Once'];

  function print() {
    const win = window.open('', '_blank', 'width=800,height=600');
    if (!win) return;
    win.document.write('<html><head><title>e-Prescription</title><style>body{font-family:Arial,sans-serif;max-width:700px;margin:30px auto}table{width:100%;border-collapse:collapse;margin:16px 0}th,td{padding:8px 10px;border:1px solid #e2e8f0;text-align:left}th{background:#f1f5f9;font-weight:700}.header{text-align:center;border-bottom:2px solid #0d6e6a;padding-bottom:12px;margin-bottom:20px}</style></head><body>');
    win.document.write('<div class="header"><h2>e-Prescription (Telemedicine)</h2><p>Dr. Name · Reg. No: XXXXXX · Date: ' + new Date().toLocaleDateString('en-IN') + '</p></div>');
    win.document.write('<p><strong>Patient:</strong> ' + (patient?.patient || '') + ' | <strong>Age:</strong> ' + (patient?.age || '') + ' | <strong>Type:</strong> Telemedicine Consultation</p>');
    win.document.write('<table><thead><tr><th>Drug</th><th>Dose</th><th>Frequency</th><th>Days</th><th>Instructions</th></tr></thead><tbody>');
    rxLines.forEach(r => { win.document.write('<tr><td>' + r.drug + '</td><td>' + r.dose + '</td><td>' + r.freq + '</td><td>' + r.days + '</td><td>' + r.instructions + '</td></tr>'); });
    win.document.write('</tbody></table>');
    if (advice) win.document.write('<p><strong>Advice:</strong> ' + advice + '</p>');
    win.document.write('<p><strong>Follow-up:</strong> ' + followUp + '</p>');
    win.document.write('<p style="margin-top:40px;color:#64748b;font-size:12px">This is a valid e-prescription issued via telemedicine as per MoHFW Telemedicine Guidelines 2020 (NMC Act).</p>');
    win.document.write('</body></html>'); win.document.close(); win.print();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="card" style={{ width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: '#0d6e6a' }}>💊 e-Prescription — {patient?.patient}</h3>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
        <button type="button" className="btn btn-secondary btn-sm mb" onClick={() => setRxLines(r => [...r, { drug: '', dose: '', freq: 'OD', days: 5, instructions: 'After meals' }])}>+ Add Medicine</button>
        {rxLines.map((line, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 60px 2fr 30px', gap: 6, marginBottom: 8, alignItems: 'center' }}>
            <input className="input" placeholder="Drug name" value={line.drug} onChange={e => setRxLines(r => r.map((l, j) => j === i ? { ...l, drug: e.target.value } : l))} />
            <input className="input" placeholder="Dose" value={line.dose} onChange={e => setRxLines(r => r.map((l, j) => j === i ? { ...l, dose: e.target.value } : l))} />
            <select className="input" value={line.freq} onChange={e => setRxLines(r => r.map((l, j) => j === i ? { ...l, freq: e.target.value } : l))}>{FREQ.map(f => <option key={f}>{f}</option>)}</select>
            <input type="number" className="input" placeholder="Days" value={line.days} onChange={e => setRxLines(r => r.map((l, j) => j === i ? { ...l, days: e.target.value } : l))} />
            <input className="input" placeholder="Instructions" value={line.instructions} onChange={e => setRxLines(r => r.map((l, j) => j === i ? { ...l, instructions: e.target.value } : l))} />
            {rxLines.length > 1 && <button type="button" style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16 }} onClick={() => setRxLines(r => r.filter((_, j) => j !== i))}>×</button>}
          </div>
        ))}
        <div className="field mb"><label>Advice / Instructions</label><textarea className="input" rows={2} value={advice} onChange={e => setAdvice(e.target.value)} placeholder="Dietary advice, lifestyle modifications..." /></div>
        <div className="field mb"><label>Follow-up</label><select className="input" value={followUp} onChange={e => setFollowUp(e.target.value)}><option>1 week</option><option>2 weeks</option><option>1 month</option><option>3 months</option><option>6 months</option><option>As needed (SOS)</option></select></div>
        <div style={{ padding: '10px 14px', background: '#fffbeb', borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#92400e' }}>📌 Compliant with MoHFW Telemedicine Practice Guidelines 2020 (NMC Act). Controlled substances cannot be prescribed via telemedicine.</div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-secondary" onClick={print}>🖨 Print Rx</button>
          <button type="button" className="btn btn-primary" onClick={async () => {
            if (onSave) {
              await onSave({ patient: patient?.patient, rxLines, advice, followUp });
            }
            print();
            onClose();
          }}>✓ Send & Save Rx</button>
        </div>
      </div>
    </div>
  );
}

export default function TelemedicinePage() {
  const { records: queueRecords, create, patch } = useHmsRecords('telemedicine-queue');
  const { create: createConsult } = useHmsRecords('telemedicine-consult');
  const [tab, setTab] = useState('queue');
  const [activeCall, setActiveCall] = useState(null);
  const [showRx, setShowRx] = useState(null);
  const [bookForm, setBookForm] = useState({ patient: '', age: '', gender: 'F', phone: '', complaint: '', doctor: '', scheduled: '', type: 'Follow-up', city: '' });
  const [showBook, setShowBook] = useState(false);

  const queue = queueRecords;

  const saveConsultNotes = async (body) => {
    await createConsult({
      ...body,
      status: 'Completed',
      at: new Date().toISOString(),
    });
  };

  const saveRx = async ({ patient: patientName, patientId, rxLines, advice, followUp }) => {
    const recordId = `EMR-TELE-${Date.now()}`;
    await ipApi.saveEmr({
      recordId,
      patientId: patientId || '',
      patientName,
      visitType: 'Telemedicine',
      status: 'completed',
      diagnosis: advice || 'Telemedicine consult',
      assessment: { primary: advice || 'Telemedicine consult' },
      rxLines: (rxLines || []).filter((l) => l.drug),
      prescription: (rxLines || []).filter((l) => l.drug).map((l) => `${l.drug} ${l.dose} ${l.freq} x ${l.days}d — ${l.instructions}`).join('; '),
      plan: { advice: advice || '', followUp: followUp || '' },
      followUp,
      createdAt: new Date().toISOString(),
    });
  };

  const updateStatus = async (id, status) => {
    const row = queue.find((q) => q.id === id || q.recordId === id);
    if (!row) return;
    await patch(row.recordId || row.id, { status });
  };

  const bookConsult = async (e) => {
    e.preventDefault();
    const id = `TELE-${Date.now()}`;
    await create({
      id,
      ...bookForm,
      age: Number(bookForm.age) || 0,
      status: 'Waiting',
    });
    setShowBook(false);
    setBookForm({ patient: '', age: '', gender: 'F', phone: '', complaint: '', doctor: '', scheduled: '', type: 'Follow-up', city: '' });
  };

  const statusColor = { Waiting: '#f59e0b', 'In Call': '#10b981', Completed: '#6366f1', Cancelled: '#94a3b8' };

  return (
    <PageShell
      title="Telemedicine / Video Consult"
      subtitle="Virtual OPD · e-Prescription · MoHFW Compliant · Secure Video Calls"
      actions={<button type="button" className="btn btn-primary" onClick={() => setShowBook((s) => !s)}>+ Book Video Consult</button>}
    >
      {showBook && (
        <form className="card mb" onSubmit={bookConsult} style={{ borderLeft: '4px solid #6366f1' }}>
          <h3 style={{ marginTop: 0 }}>Book Video Consult</h3>
          <div className="form-grid mb">
            <div className="field"><label>Patient Name *</label><input className="input" required value={bookForm.patient} onChange={(e) => setBookForm((f) => ({ ...f, patient: e.target.value }))} /></div>
            <div className="field"><label>Age</label><input type="number" className="input" value={bookForm.age} onChange={(e) => setBookForm((f) => ({ ...f, age: e.target.value }))} /></div>
            <div className="field"><label>Gender</label><select className="input" value={bookForm.gender} onChange={(e) => setBookForm((f) => ({ ...f, gender: e.target.value }))}><option value="M">Male</option><option value="F">Female</option></select></div>
            <div className="field"><label>Phone</label><input className="input" value={bookForm.phone} onChange={(e) => setBookForm((f) => ({ ...f, phone: e.target.value }))} /></div>
            <div className="field"><label>Doctor</label><input className="input" value={bookForm.doctor} onChange={(e) => setBookForm((f) => ({ ...f, doctor: e.target.value }))} placeholder="Dr. Sharma" /></div>
            <div className="field"><label>Scheduled Time</label><input className="input" value={bookForm.scheduled} onChange={(e) => setBookForm((f) => ({ ...f, scheduled: e.target.value }))} placeholder="10:30" /></div>
            <div className="field full"><label>Complaint</label><input className="input" value={bookForm.complaint} onChange={(e) => setBookForm((f) => ({ ...f, complaint: e.target.value }))} /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowBook(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save Booking</button>
          </div>
        </form>
      )}

      <div className="info-banner mb">
        Queue, e-prescription, and consultation notes are saved to HCEN. Live video requires a WebRTC provider (e.g. Twilio, Daily.co) — preview room below is for workflow only.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Scheduled Today', val: queue.length, color: '#6366f1' }, { label: 'Waiting', val: queue.filter(q => q.status === 'Waiting').length, color: '#f59e0b' }, { label: 'In Call', val: queue.filter(q => q.status === 'In Call').length, color: '#10b981' }, { label: 'Completed', val: queue.filter(q => q.status === 'Completed').length, color: '#3b82f6' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="period-tabs card mb">
        {[{ id: 'queue', label: '📋 Patient Queue' }, { id: 'call', label: '📹 Video Room', disabled: !activeCall }].map(({ id, label, disabled }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => !disabled && setTab(id)} style={{ opacity: disabled ? 0.4 : 1 }}>{label}</button>
        ))}
      </div>

      {tab === 'queue' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {queue.map(p => (
            <div key={p.id || p.recordId} className="card" style={{ borderLeft: `4px solid ${statusColor[p.status] || '#94a3b8'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15 }}>{p.patient} <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b' }}>{p.age}y / {p.gender === 'M' ? 'Male' : 'Female'} · {p.city}</span></div>
                  <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>📋 {p.complaint}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{p.doctor} · {p.scheduled} · {p.type}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: (statusColor[p.status] || '#94a3b8') + '18', color: statusColor[p.status] || '#94a3b8', border: `1px solid ${(statusColor[p.status] || '#94a3b8')}40` }}>{p.status}</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {p.status !== 'Completed' && <button type="button" className="btn btn-primary btn-sm" onClick={async () => { setActiveCall(p); await updateStatus(p.id || p.recordId, 'In Call'); setTab('call'); }}>📹 Start Call</button>}
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowRx(p)}>💊 e-Prescribe</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'call' && activeCall && (
        <VideoCallRoom
          patient={activeCall}
          onSaveNotes={saveConsultNotes}
          onEnd={async () => { await updateStatus(activeCall.id || activeCall.recordId, 'Completed'); setActiveCall(null); setTab('queue'); }}
        />
      )}

      {showRx && <EPrescribeModal patient={showRx} onClose={() => setShowRx(null)} onSave={saveRx} />}
    </PageShell>
  );
}
